import { action, computed, makeObservable, observable } from 'mobx'

import { captureException } from '@sentry/core'
import type { DateTime } from 'luxon'
import { fetchCatalogsAccessibleByUsers } from '../firestore/Catalog'
import {
  createSection,
  fetchInstructorSections,
  fetchSectionsForInstructors,
  getSection,
  getSectionsStreamForInstructor,
  getSectionsStreamForInstructors,
  SectionPaymentMethod,
  SectionState,
} from '../firestore/Section'
import {
  createDraftAssignment,
  createSectionAssignment,
  getAssignmentWithLatestExpiresAt,
  type FirestoreSectionAssignment,
} from '../firestore/SectionAssignment'
import type { FirestoreSectionRequest } from '../firestore/SectionRequest'
import {
  createSectionInvoiceRequest,
  SectionRequestState,
  SectionRequestType,
} from '../firestore/SectionRequest'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { fetchSlides, getSlides } from '../firestore/Slide'
import { getSlideDeck } from '../firestore/SlideDeck'
import { getSlideDeckAuthors } from '../firestore/SlideDeckAuthor'
import {
  fetchSlideDeckMaterialsForInstructor,
  getAllSlideDeckMaterialsForInstructor,
} from '../firestore/SlideDeckMaterial'
import { getSlideDeckReferences } from '../firestore/SlideDeckReference'
import {
  fetchSlideQuestions,
  getSlideQuestions,
} from '../firestore/SlideQuestion'
import { getSlideRubrics } from '../firestore/SlideRubric'
import { getTeachingPlan } from '../firestore/TeachingPlan'
import { fetchTAInstructorIDs } from '../firestore/UserProfile'
import {
  applyApprovedFreeAdminPromotion,
  redeemPromotions,
} from '../firestore/UserPromotionRedemption'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Section } from '../models/Section'
import {
  SectionAssignment,
  type AssignmentGroupingType,
  type AssignmentType,
} from '../models/SectionAssignment'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckAuthor } from '../models/SlideDeckAuthor'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideDeckReference } from '../models/SlideDeckReference'
import { SlideModel } from '../models/SlideModel'
import { SlideQuestion } from '../models/SlideQuestion'
import { SlideRubric } from '../models/SlideRubric'
import { TeachingPlan } from '../models/TeachingPlan'
import type { UserPromotion } from '../models/UserPromotion'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { UserProfileRole, type StaticModelCollection } from '../types'
import { calculateTimeLeftInSession } from '../util/time'
import { Cubit } from './core'

export class InstructorSlideDeckCubit extends Cubit {
  repository: FirebaseRepository
  forceStreamInsteadOfFetch: boolean

  slideDeckId: string

  assignmentWithLatestExpiresAt: SectionAssignment
  slideDeck: SlideDeck
  sectionPassPricing: SettingsSectionPassPricing
  slides: StaticModelCollection<SlideModel>
  questions: StaticModelCollection<SlideQuestion>
  materials: StaticModelCollection<SlideDeckMaterial>
  authors: StaticModelCollection<SlideDeckAuthor>
  rubrics: StaticModelCollection<SlideRubric>
  references: StaticModelCollection<SlideDeckReference>
  sections: StaticModelCollection<Section>
  teachingPlan: TeachingPlan
  section: Section

  instructorUserId?: string
  catalogId?: string
  teachingPlanId?: string

  @observable
  sectionId?: string

  constructor(
    repository: FirebaseRepository,
    slideDeckId: string,
    options?: {
      instructorUserId?: string
      catalogId?: string
      teachingPlanId?: string
      sectionId?: string
      forceStreamInsteadOfFetch?: boolean
    }
  ) {
    super()
    this.slideDeckId = slideDeckId
    this.instructorUserId = options?.instructorUserId
    this.catalogId = options?.catalogId
    this.sectionId = options?.sectionId
    this.teachingPlanId = options?.teachingPlanId
    this.forceStreamInsteadOfFetch = options?.forceStreamInsteadOfFetch ?? false

    makeObservable(this)

    this.repository = repository
    this.slideDeck = SlideDeck.empty(repository)
    this.slides = SlideModel.emptyCollection(repository)
    this.questions = SlideQuestion.emptyCollection(repository)
    this.materials = SlideDeckMaterial.emptyCollection(repository)
    this.authors = SlideDeckAuthor.emptyCollection(repository)
    this.rubrics = SlideRubric.emptyCollection(repository)
    this.references = SlideDeckReference.emptyCollection(repository)
    this.sections = Section.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
    this.teachingPlan = TeachingPlan.empty(repository)
    this.section = Section.empty(repository)
    this.assignmentWithLatestExpiresAt = SectionAssignment.empty(repository)
  }

  initialize(): void {
    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (pricing) => {
        this.sectionPassPricing.replaceModel(pricing)
      }
    )

    this.addStream(
      getSlideDeck(this.repository, { slideDeckId: this.slideDeckId }),
      (slideDeck) => {
        this.slideDeck.replaceModel(slideDeck)
      }
    )
    this.addStream(
      getSlideDeckAuthors(this.repository, { slideDeckId: this.slideDeckId }),
      (authors) => {
        this.authors.replaceModels(authors)
      }
    )

    this.addStream(
      getSlideRubrics(this.repository, { slideDeckId: this.slideDeckId }),
      (rubrics) => {
        this.rubrics.replaceModels(rubrics)
      }
    )

    this.addStream(
      getSlideDeckReferences(this.repository, {
        slideDeckId: this.slideDeckId,
      }),
      (references) => {
        this.references.replaceModels(references)
      }
    )

    if (this.teachingPlanId && this.catalogId) {
      this.addStream(
        getTeachingPlan(this.repository, {
          catalogId: this.catalogId,
          teachingPlanId: this.teachingPlanId,
        }),
        (teachingPlan) => {
          this.teachingPlan.replaceModel(teachingPlan)
        }
      )
    }

    this.restartSectionStream()

    const user = this.repository.breakoutUser

    this.forceStreamInsteadOfFetch
      ? this.setupQuestionStream()
      : this.fetchQuestions()
    this.forceStreamInsteadOfFetch
      ? this.setupMaterialsStream()
      : this.fetchMaterials()
    this.forceStreamInsteadOfFetch
      ? this.setupSlidesStream()
      : this.fetchSlides()

    if (user?.role === UserProfileRole.ta) {
      this.startSectionsStreamForTA()
    } else {
      this.addStream(
        getSectionsStreamForInstructor(
          this.repository,
          this.instructorUserId
            ? {
                userId: this.instructorUserId,
              }
            : undefined
        ),
        (sections) => {
          // filter our closed sections
          sections = sections.filter(
            (section) => section.data.sectionState !== SectionState.completed
          )
          this.sections.replaceModels(sections)
        }
      )
    }
  }

  @action
  setSectionId(sectionId: string) {
    this.sectionId = sectionId
    this.restartSectionStream()
  }

  restartSectionStream() {
    this.removeStream('section')
    this.removeStream('latest-assignment')
    if (this.sectionId) {
      this.addStream(
        getSection(this.repository, {
          sectionId: this.sectionId,
        }),
        (section) => {
          this.section.replaceModel(section)
        },
        { name: 'section' }
      )

      this.addStream(
        getAssignmentWithLatestExpiresAt(this.repository, {
          sectionId: this.sectionId,
        }),
        (assignment) => {
          this.assignmentWithLatestExpiresAt.replaceModel(assignment)
        },
        { name: 'latest-assignment' }
      )
    } else {
      this.section.replaceModel(Section.empty(this.repository))
      this.assignmentWithLatestExpiresAt.replaceModel(
        SectionAssignment.empty(this.repository)
      )
    }
  }

  @computed
  get allowSectionPassPaymentMethod() {
    return (
      this.sectionPassPricing.hasData &&
      this.sectionPassPricing.data.sectionPassPrice > 0
    )
  }

  @computed
  get questionsSorted() {
    const slideIds = this.slidesSorted.map((slide) => slide.id)

    return sortQuestions({
      questions: this.questions.models,
      slideIds,
    })
  }

  @computed
  get slidesSorted() {
    return this.slides.models.sort(
      (a, b) => a.data.slideOrder - b.data.slideOrder
    )
  }

  @computed
  get sortedSectionOptions() {
    // Returns a list of sections sorted by class and section name.
    // Returned in a format that can be used by the BreakoutSelect component.
    return this.sections.models
      .map((section) => ({
        value: section.id,
        label: `${section.data.className}: ${section.data.sectionName}`,
        instructorUserId: section.data.instructorUserId,
        hasSectionDates:
          section.data.sectionStartDate && section.data.sectionEndDate
            ? true
            : false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  sortSections = (sections: Section[]) => {
    return sections.sort((a: Section, b: Section) => {
      if (a.data.sectionState === b.data.sectionState) {
        if (
          a.data.updatedAt.getMilliseconds() ===
          b.data.updatedAt.getMilliseconds()
        ) {
          if (a.data.className !== b.data.className) {
            return a.data.className.localeCompare(b.data.className)
          } else {
            return a.data.sectionName.localeCompare(b.data.sectionName)
          }
        }
        return a.data.updatedAt!.getTime() - b.data.updatedAt!.getTime()
      }
      return a.data.sectionState - b.data.sectionState
    })
  }

  @computed
  get estimatedExperienceDurationInMinutes(): number {
    const durationInMinutes = (slide: SlideModel) => {
      let duration = 0
      if (slide.data.slideVideoDuration) {
        duration = Math.ceil(slide.data.slideVideoDuration / 60)
      } else {
        duration = Math.ceil(slide.data.slideDuration / 60)
      }
      return duration
    }

    return calculateTimeLeftInSession({
      slides: this.slides._models.map((slide) => ({
        durationInMinutes: durationInMinutes(slide),
      })),
      roundToNearest: 5,
    })
  }

  async setupSlidesStream() {
    const stream = getSlides(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (slides) => {
      this.slides.replaceModels(slides)
    })
  }

  async fetchSlides() {
    try {
      const slides = await fetchSlides(this.repository, {
        slideDeckId: this.slideDeckId,
      })
      this.slides.replaceModels(slides)
    } catch (error) {
      captureException(error)
      this.slides.replaceModels([])
    }
  }

  async setupMaterialsStream() {
    const stream = getAllSlideDeckMaterialsForInstructor(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (materials) => {
      this.materials.replaceModels(materials)
    })
  }

  async fetchMaterials() {
    try {
      const data = await fetchSlideDeckMaterialsForInstructor(this.repository, {
        slideDeckId: this.slideDeckId,
      })
      this.materials.replaceModels(data)
    } catch (error) {
      captureException(error)
      this.materials.replaceModels([])
    }
  }

  async setupQuestionStream() {
    const stream = getSlideQuestions(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (questions) => {
      this.questions.replaceModels(questions)
    })
  }

  async fetchQuestions() {
    try {
      const data = await fetchSlideQuestions(this.repository, {
        slideDeckId: this.slideDeckId,
      })

      this.questions.replaceModels(data)
    } catch (error) {
      captureException(error)
      this.questions.replaceModels([])
    }
  }

  async fetchSections(
    catalogId: string,
    role: string,
    instructorUserId?: string
  ) {
    if (role !== UserProfileRole.ta) {
      return fetchInstructorSections(this.repository, { instructorUserId })
    }

    /* TA only logic */

    // get instructors which can access the catalog
    const instructorIds = await fetchTAInstructorIDs(this.repository)

    // get catalogs accessible by the instructors
    const catalogsAccessibleByInstructorId =
      await fetchCatalogsAccessibleByUsers(this.repository, {
        userIds: instructorIds,
      })

    // prune instructors which cannot access the current catalog
    const instructorsWithCatalogAccess = instructorIds.filter(
      (instructorId) => {
        const instructorCatalogs =
          instructorId in catalogsAccessibleByInstructorId
            ? catalogsAccessibleByInstructorId[instructorId]
            : []

        // todo(ashold12): the dart app renders all TA sections, however this is potentially incorrect as all instructors may not have
        // access to the current catalog. Here I am pruning sections of profs which do not have access to the current catalog.
        // however, if they have no catalogs I am assuming they are an admin and rendering anyways. This is not perfect
        // but I believe it's an improvement over the previous implementation as we cannot interrogate the user's role
        return (
          instructorCatalogs.includes(catalogId) ||
          instructorCatalogs.length === 0
        )
      }
    )
    // fetch sections for the pruned instructors
    return await fetchSectionsForInstructors(this.repository, {
      instructorIds: instructorsWithCatalogAccess,
    })
  }

  async startSectionsStreamForTA() {
    const catalogId = this.catalogId

    if (!catalogId) return

    /* TA only logic */
    // get instructors which can access the catalog
    const instructorIds = await fetchTAInstructorIDs(this.repository)

    // get catalogs accessible by the instructors
    const catalogsAccessibleByInstructorId =
      await fetchCatalogsAccessibleByUsers(this.repository, {
        userIds: instructorIds,
      })

    // prune instructors which cannot access the current catalog
    const instructorsWithCatalogAccess = instructorIds.filter(
      (instructorId) => {
        const instructorCatalogs =
          instructorId in catalogsAccessibleByInstructorId
            ? catalogsAccessibleByInstructorId[instructorId]
            : []

        // todo(ashold12): the dart app renders all TA sections, however this is potentially incorrect as all instructors may not have
        // access to the current catalog. Here I am pruning sections of profs which do not have access to the current catalog.
        // however, if they have no catalogs I am assuming they are an admin and rendering anyways. This is not perfect
        // but I believe it's an improvement over the previous implementation as we cannot interrogate the user's role
        return (
          instructorCatalogs.includes(catalogId) ||
          instructorCatalogs.length === 0
        )
      }
    )
    const stream = getSectionsStreamForInstructors(
      this.repository,
      instructorsWithCatalogAccess
    )

    this.addStream(stream, (sections) => {
      // filter our closed sections
      sections = sections.filter(
        (section) => section.data.sectionState !== SectionState.completed
      )
      this.sections.replaceModels(sections)
    })
  }

  async createDraftSectionAssignment(params: {
    sectionId: string
    assignmentType: AssignmentType
    slideDeckId: string
    assignedAt?: DateTime
    expiresAt?: DateTime
    catalogId?: string
    groupingType: AssignmentGroupingType
    groupingSize?: number
    groupingSizeMaximum?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
  }) {
    const isGrouping = this.repository.featureFlags.data.showInstructorGroupsTab
    return createDraftAssignment(this.repository, { ...params, isGrouping })
  }

  async createSectionAssignment(params: {
    sectionId: string
    assignmentType: AssignmentType
    assignedAt: DateTime
    expiresAt: DateTime
    groupingType: AssignmentGroupingType
    slideDeckId: string
    groupingSizeMaximum?: number
    catalogId?: string
    groupingSize?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
  }) {
    const isGrouping = this.repository.featureFlags.data.showInstructorGroupsTab
    return createSectionAssignment(this.repository, { ...params, isGrouping })
  }

  createSectionInvoiceRequest = async ({
    sectionId,
    sectionRequest,
  }: {
    sectionId: string
    sectionRequest: Pick<
      FirestoreSectionRequest,
      | 'sectionRequestAssignmentCount'
      | 'sectionRequestStudentCount'
      | 'requestedAt'
      | 'organizationId'
      | 'sectionId'
      | 'updatedByUserId'
    >
  }) => {
    await createSectionInvoiceRequest(this.repository, sectionId, {
      ...sectionRequest,
      userId: this.repository.uid,
      sectionRequestType: SectionRequestType.invoice,
      sectionRequestState: SectionRequestState.pending,
      updatedAt: new Date(),
      requestedAt: new Date(),
    })
  }

  createSection = async ({
    className,
    sectionName,
    organizationId,
    sectionPaymentMethod,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
  }: {
    className: string
    sectionName: string
    organizationId?: string
    sectionPaymentMethod?: SectionPaymentMethod
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
  }) => {
    const doc = await createSection(this.repository, {
      className,
      sectionName,
      organizationId,
      userId: this.instructorUserId || this.repository.uid,
      sectionPaymentMethod: sectionPaymentMethod ?? SectionPaymentMethod.token,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
    })
    return doc.id
  }

  redeemPromotions = async (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => {
    await Promise.all(
      userPromotions.map((userPromotion) =>
        redeemPromotions(this.repository, {
          userId: userPromotion.data.userId,
          promotionId: userPromotion.data.promotionId,
          userPromotionId: userPromotion.id,
          sectionId,
        })
      )
    )
    return
  }

  applyApprovedFreeAdminPromotion = async ({
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }) => {
    return await applyApprovedFreeAdminPromotion(this.repository, {
      userId,
      sectionId,
    })
  }
}
